import React, { ReactNode } from 'react';

import { Link } from 'react-router-dom';

import { CellContext } from '@tanstack/react-table';

import {
  formatCurrency,
  Currency,
  parseCurrency,
} from 'domains-billing-currency';

import { longDateNoTime } from 'lane-shared/helpers/formatters';

import { SupportedLocaleEnum } from 'localization';

import {
  RenderType,
  type Column,
  type DefaultRenderColumn,
} from './hooks/useTable';
import useUserLocale from 'lane-shared/hooks/useUserLocale';

type cellRenderersType = {
  [RenderType.text]: (cell: string) => ReactNode;
  [RenderType.link]: (cell: { href: string; label: string }) => ReactNode;
  [RenderType.currency]: (cell: number, currency?: Currency) => ReactNode;
  [RenderType.date]: (cell: string) => ReactNode;
};

type getCellRenderersType = (locale: SupportedLocaleEnum) => cellRenderersType;

const getCellRenderers: getCellRenderersType = locale => ({
  text: cell => cell || '',
  link: cell => (cell ? <Link to={cell.href}>{cell.label}</Link> : ''),
  currency: (cell, currency = Currency.USD) =>
    cell ? formatCurrency(currency, { locale })(cell) : '',
  date: cell => (cell ? longDateNoTime(cell) : ''),
});

type CellRendererType<TDataShape> = {
  column: Column<TDataShape>;
  cell: CellContext<TDataShape, any>;
};

type CellType = string | { href: string; label: string } | number;

function isLinkCell(cell: CellType): cell is { href: string; label: string } {
  return (
    typeof cell === 'object' &&
    cell !== null &&
    'href' in cell &&
    'label' in cell &&
    typeof cell.href === 'string' &&
    typeof cell.label === 'string'
  );
}

function isDefaultRenderColumn<TDataShape>(
  column: Column<TDataShape>
): column is DefaultRenderColumn {
  return 'type' in column;
}

function getCurrencyFromRow<TDataShape>(
  row: TDataShape,
  column: Column<TDataShape>
): Currency {
  if ('type' in column && column.type === 'currency' && column.currency) {
    if (typeof column.currency === 'function') {
      return column.currency(row);
    }

    return column.currency;
  }

  // Try to extract currency from common field names in the row
  if (
    typeof row === 'object' &&
    row !== null &&
    'currency' in row &&
    row.currency !== undefined
  ) {
    const currencyValue = row.currency;

    // Only parse if it's a string (GraphQL enums and Currency enums are strings)
    if (typeof currencyValue === 'string') {
      return parseCurrency(currencyValue);
    }
  }

  // Fallback to USD for backward compatibility
  return Currency.USD;
}

function renderCurrencyCell<TDataShape>(
  cellContents: CellType,
  row: TDataShape,
  column: Column<TDataShape>,
  locale: SupportedLocaleEnum
): ReactNode | null {
  if (typeof cellContents !== 'number') {
    return null;
  }

  const currency = getCurrencyFromRow(row, column);
  const currencyRenderer = getCellRenderers(locale).currency;

  return currencyRenderer(cellContents, currency);
}

function renderTextCell(
  cellContents: CellType,
  locale: SupportedLocaleEnum
): ReactNode | null {
  if (typeof cellContents !== 'string') {
    return null;
  }

  const renderer = getCellRenderers(locale).text;

  return renderer(cellContents);
}

function renderDateCell(
  cellContents: CellType,
  locale: SupportedLocaleEnum
): ReactNode | null {
  // Handle Date objects by converting to ISO string
  if (cellContents instanceof Date) {
    const renderer = getCellRenderers(locale).date;

    return renderer(cellContents.toISOString());
  }

  if (typeof cellContents !== 'string') {
    return null;
  }

  const renderer = getCellRenderers(locale).date;

  return renderer(cellContents);
}

function renderLinkCell(
  cellContents: CellType,
  locale: SupportedLocaleEnum
): ReactNode | null {
  if (!isLinkCell(cellContents)) {
    return null;
  }

  const renderer = getCellRenderers(locale).link;

  return renderer(cellContents);
}

function renderTypedCell<TDataShape>(
  cellContents: CellType,
  row: TDataShape,
  column: Column<TDataShape>,
  locale: SupportedLocaleEnum
): ReactNode | null {
  if (!isDefaultRenderColumn(column)) {
    return null;
  }

  if (column.type === RenderType.currency) {
    return renderCurrencyCell(cellContents, row, column, locale);
  }

  if (column.type === RenderType.text) {
    return renderTextCell(cellContents, locale);
  }

  if (column.type === RenderType.date) {
    return renderDateCell(cellContents, locale);
  }

  if (column.type === RenderType.link) {
    return renderLinkCell(cellContents, locale);
  }

  return null;
}

export const CellRenderer = <TDataShape,>({
  column,
  cell,
}: CellRendererType<TDataShape>) => {
  const locale = useUserLocale();

  if ('renderCell' in column) {
    return column.renderCell(cell.getValue(), cell.row.original);
  }

  if ('type' in column) {
    const result = renderTypedCell(
      cell.getValue(),
      cell.row.original,
      column,
      locale
    );

    if (result !== null) {
      return result;
    }
  }

  return cell.getValue();
};
